import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  AppFeature,
  AppFeatureListData,
  CreateAppFeaturePayload,
} from '@/client/types/feature-flags/app-features';
import { JsonEnvelope } from '@/client/types/feature-flags';

const BASE = '/api/admin/app_features';

export const appFeatureKeys = {
  all: ['app-features'] as const,
  list: (activeOnly: boolean) => [...appFeatureKeys.all, 'list', activeOnly] as const,
  detail: (code: string) => [...appFeatureKeys.all, 'detail', code] as const,
};

// ─── List ────────────────────────────────────────────────────────────────────

const useAppFeaturesFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (activeOnly: boolean): Promise<AppFeature[]> => {
    const res = await request<JsonEnvelope<AppFeatureListData>>(
      `${BASE}?active_only=${activeOnly}`
    );
    return res?.data?.features ?? [];
  }, 'An unexpected error occurred while fetching app features');
};

/** All app features, optionally including inactive ones. */
export const useAppFeaturesQuery = (activeOnly = true, enabled = true) => {
  const queryFn = useAppFeaturesFn();
  return useQuery({
    queryKey: appFeatureKeys.list(activeOnly),
    queryFn: () => queryFn(activeOnly),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Create ──────────────────────────────────────────────────────────────────
// NOTE: the create body is JSON, so `admin/app_features` must be in the proxy's
// JSON allowlist (src/server/lib/utils.ts) or it silently no-ops.

export const useCreateAppFeatureMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: withErrorHandling(
      (payload: CreateAppFeaturePayload) =>
        request<JsonEnvelope<AppFeature>>(`${BASE}/`, 'POST', {
          order_by: 0,
          ...payload,
        }),
      'An unexpected error occurred while creating the app feature'
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: appFeatureKeys.all }),
  });
};
